import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Clock, X, Check, Mail, Send, AlertCircle, ExternalLink, Smartphone, Info } from 'lucide-react';

const NOTIFICATION_SETTINGS_KEY = 'dmu-rimp-notification-settings';

// In-app toast notification component
const ToastNotification = ({ message, isVisible, onClose }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(onClose, 4000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 left-4 right-4 z-[10010] animate-fade-in">
      <div className="bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] text-white p-4 rounded-xl shadow-2xl flex items-start gap-3 max-w-md mx-auto">
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-sm">🙏 DMU Lenten Retreat</p>
          <p className="text-white/90 text-sm mt-1">{message}</p>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const NotificationSettingsModal = ({ isOpen, onClose, currentDay }) => {
  const [settings, setSettings] = useState({
    browserEnabled: false,
    reminderTime: '07:00'
  });
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [showSuccess, setShowSuccess] = useState(false);
  const [serviceWorkerStatus, setServiceWorkerStatus] = useState('checking'); // 'checking', 'ready', 'unavailable'
  const [testSent, setTestSent] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [nativeNotificationsAvailable, setNativeNotificationsAvailable] = useState(true);

  // Show in-app toast notification
  const showInAppToast = useCallback((message) => {
    setToastMessage(message);
    setShowToast(true);
  }, []);

  const closeToast = useCallback(() => {
    setShowToast(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    // Load saved settings
    const savedSettings = localStorage.getItem(NOTIFICATION_SETTINGS_KEY);
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings));
      } catch (e) {
        console.error('[Notifications] Failed to parse settings:', e);
      }
    }

    // Check notification permission with error handling
    let notificationsSupported = false;
    try {
      if ('Notification' in window) {
        notificationsSupported = true;
        setNotificationPermission(Notification.permission);
      } else {
        setNativeNotificationsAvailable(false);
      }
    } catch (e) {
      console.error('[Notifications] Notification API error:', e);
      setNativeNotificationsAvailable(false);
    }

    // Check service worker status with timeout
    const checkServiceWorker = async () => {
      if (!('serviceWorker' in navigator)) {
        setServiceWorkerStatus('unavailable');
        return;
      }

      // Set a timeout for service worker ready
      const timeout = setTimeout(() => {
        console.warn('[Notifications] Service Worker ready timeout');
        setServiceWorkerStatus('unavailable');
      }, 3000); // 3 second timeout

      try {
        await navigator.serviceWorker.ready;
        clearTimeout(timeout);
        setServiceWorkerStatus('ready');
        console.log('[Notifications] Service Worker ready');
      } catch (error) {
        clearTimeout(timeout);
        console.error('[Notifications] Service Worker error:', error);
        setServiceWorkerStatus('unavailable');
      }
    };

    checkServiceWorker();
  }, [isOpen]);

  // Re-schedule reminder when settings change and notifications are enabled
  useEffect(() => {
    if (settings.browserEnabled && serviceWorkerStatus === 'ready') {
      scheduleNextReminder(settings.reminderTime);
    }
  }, [settings.reminderTime, settings.browserEnabled, serviceWorkerStatus]);

  const requestBrowserPermission = async () => {
    try {
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);
        return permission === 'granted';
      }
    } catch (e) {
      console.error('[Notifications] Permission request failed:', e);
      setNativeNotificationsAvailable(false);
    }
    return false;
  };

  const scheduleNextReminder = async (reminderTime) => {
    if (serviceWorkerStatus !== 'ready') return;

    const [hours, minutes] = reminderTime.split(':').map(Number);
    const now = new Date();
    const reminderDate = new Date(now);
    reminderDate.setHours(hours, minutes, 0, 0);

    // If the time has passed today, schedule for tomorrow
    if (reminderDate <= now) {
      reminderDate.setDate(reminderDate.getDate() + 1);
    }

    const delay = reminderDate.getTime() - now.getTime();

    try {
      const registration = await navigator.serviceWorker.ready;
      if (registration.active) {
        registration.active.postMessage({
          type: 'SCHEDULE_REMINDER',
          delay: delay,
          title: '🙏 DMU Lenten Retreat',
          body: `Day ${currentDay || ''}: Time for your daily prayer and reflection.`,
          currentDay: currentDay
        });
        console.log('[Notifications] Reminder scheduled for', new Date(reminderDate).toLocaleString());
      }
    } catch (error) {
      console.error('[Notifications] Failed to schedule reminder:', error);
    }
  };

  const handleBrowserToggle = async () => {
    if (!settings.browserEnabled) {
      // Turning ON
      const newSettings = { ...settings, browserEnabled: true };
      setSettings(newSettings);
      localStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(newSettings));

      // Try to get native permission if available
      if (nativeNotificationsAvailable && notificationPermission !== 'denied') {
        const granted = await requestBrowserPermission();
        if (granted && serviceWorkerStatus === 'ready') {
          // Try native notification
          try {
            const registration = await navigator.serviceWorker.ready;
            if (registration.active) {
              registration.active.postMessage({
                type: 'SHOW_NOTIFICATION',
                title: '✅ Notifications Enabled!',
                body: 'You will receive daily prayer reminders. God bless your retreat journey!',
                tag: 'dmu-test-notification',
                url: '/'
              });
            }
          } catch (e) {
            // Fallback to regular notification
            try {
              new Notification("DMU Lenten Retreat", {
                body: "Notifications enabled! God bless your retreat journey. 🙏",
                icon: "/logo192.png"
              });
            } catch (e2) {
              showInAppToast("Reminders enabled! You'll be notified at your chosen time.");
            }
          }
          scheduleNextReminder(settings.reminderTime);
        } else {
          // Permission not granted, show in-app toast
          showInAppToast("Reminders enabled! You'll be notified at your chosen time.");
        }
      } else {
        // Native not available, show in-app toast
        showInAppToast("Reminders enabled! You'll be notified at your chosen time.");
      }
    } else {
      // Turning OFF
      const newSettings = { ...settings, browserEnabled: false };
      setSettings(newSettings);
      localStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(newSettings));
      showInAppToast("Daily reminders disabled.");
    }
  };

  const handleTimeChange = (newTime) => {
    const newSettings = { ...settings, reminderTime: newTime };
    setSettings(newSettings);
    localStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(newSettings));
  };

  const sendTestNotification = async () => {
    setTestSent(true);
    
    const testMessage = `Time for your Daily Examen. Day ${currentDay || 1}: Take a moment to reflect on God's presence today.`;

    // Try native notification first
    let nativeSuccess = false;
    
    if (nativeNotificationsAvailable && notificationPermission === 'granted') {
      try {
        if (serviceWorkerStatus === 'ready') {
          const registration = await navigator.serviceWorker.ready;
          if (registration.active) {
            registration.active.postMessage({
              type: 'SHOW_NOTIFICATION',
              title: '🔔 Test Notification',
              body: testMessage,
              tag: 'dmu-test-notification',
              url: currentDay ? `/day/${currentDay}` : '/'
            });
            nativeSuccess = true;
          }
        }
        
        if (!nativeSuccess) {
          new Notification("🔔 Test Notification", {
            body: testMessage,
            icon: "/logo192.png"
          });
          nativeSuccess = true;
        }
      } catch (e) {
        console.log('[Notifications] Native notification failed, using fallback');
      }
    }

    // Always show in-app toast as visual feedback
    showInAppToast(testMessage);

    setTimeout(() => setTestSent(false), 3000);
  };

  // Email Myself - opens native email app
  const handleEmailMyself = () => {
    const appUrl = window.location.origin;
    const dayUrl = currentDay ? `${appUrl}/day/${currentDay}` : appUrl;
    
    const subject = encodeURIComponent("🙏 Daily Prayer Reminder - DMU Lenten Retreat");
    const body = encodeURIComponent(
`Hi there!

This is your daily reminder to spend time in prayer.

Your 30-Day Ignatian Lenten Journey awaits. Take a few moments to:
• Read today's Scripture
• Reflect on God's message for you
• Journal your insights
• End with the Examen

Open the app: ${dayUrl}

"Be still and know that I am God." - Psalm 46:10

God bless your retreat journey!
— Divine Mercy University`
    );
    
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  // Schedule email reminder (opens calendar with email reminder)
  const handleScheduleReminder = () => {
    const appUrl = window.location.origin;
    
    const title = encodeURIComponent("🙏 DMU Lenten Retreat - Daily Prayer");
    const details = encodeURIComponent(
`Time for your daily prayer and reflection!

Open the retreat app: ${appUrl}

• Read Scripture
• Reflect & Pray  
• Journal your insights
• Evening Examen`
    );
    
    const [hours, minutes] = settings.reminderTime.split(':');
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    const endTime = new Date(tomorrow);
    endTime.setMinutes(endTime.getMinutes() + 30);
    
    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&dates=${formatDate(tomorrow)}/${formatDate(endTime)}&recur=RRULE:FREQ=DAILY;COUNT=30`;
    
    window.open(calendarUrl, '_blank');
  };

  // Get status text for service worker
  const getStatusText = () => {
    if (serviceWorkerStatus === 'checking') return 'Checking...';
    if (serviceWorkerStatus === 'ready') return '✓ Ready';
    return 'Preview mode';
  };

  // Check if native notifications are blocked
  const isNativeBlocked = notificationPermission === 'denied' || !nativeNotificationsAvailable;

  if (!isOpen) return null;

  return (
    <>
      <ToastNotification 
        message={toastMessage} 
        isVisible={showToast} 
        onClose={closeToast} 
      />
      
      <div className="fixed inset-0 z-[10001] bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-[var(--card-bg)] rounded-2xl max-w-md w-full overflow-hidden shadow-xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] p-5 text-white sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-6 h-6" />
                <h2 className="font-heading text-lg font-bold">Daily Reminders</h2>
              </div>
              <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-5 space-y-5">
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Choose how you'd like to be reminded for daily prayer.
            </p>

            {/* Reminder Time */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Preferred Prayer Time
              </label>
              <input
                type="time"
                value={settings.reminderTime}
                onChange={(e) => handleTimeChange(e.target.value)}
                className="w-full p-3 border border-gray-200 dark:border-[var(--border)] dark:bg-[var(--muted)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
                data-testid="notification-time"
              />
            </div>

            {/* Reminder Options */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Reminder Options</h3>
              
              {/* Push Notifications Toggle */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                      <Bell className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 dark:text-gray-200 text-sm">Push Notifications</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {getStatusText()}
                      </p>
                    </div>
                  </div>
                  {/* Toggle - Always functional for visual state */}
                  <button
                    onClick={handleBrowserToggle}
                    className={`w-14 h-8 rounded-full transition-all duration-200 ${
                      settings.browserEnabled 
                        ? 'bg-[var(--primary)]' 
                        : 'bg-gray-300 dark:bg-gray-600'
                    } flex items-center ${settings.browserEnabled ? 'justify-end' : 'justify-start'} px-1`}
                    data-testid="browser-notification-toggle"
                    aria-pressed={settings.browserEnabled}
                  >
                    <div className={`w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-200 ${
                      settings.browserEnabled ? 'scale-110' : 'scale-100'
                    }`} />
                  </button>
                </div>
                
                {/* Test Notification Button - Always functional */}
                <button
                  onClick={sendTestNotification}
                  className={`w-full py-2.5 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    testSent 
                      ? 'bg-green-500 text-white' 
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                  data-testid="test-notification-btn"
                >
                  <Bell className="w-4 h-4" />
                  {testSent ? 'Notification Sent! ✓' : 'Send Test Notification'}
                </button>
              </div>

              {/* Status Messages */}
              {isNativeBlocked && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-3 flex items-start gap-2">
                  <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-700 dark:text-amber-300">
                    <p className="font-medium mb-1">Native notifications unavailable</p>
                    <p>
                      {notificationPermission === 'denied' 
                        ? 'Browser notifications are blocked. Enable in browser settings or use the alternatives below.'
                        : 'This preview environment may not support push notifications. Use Email or Calendar reminders instead.'}
                    </p>
                  </div>
                </div>
              )}

              {settings.browserEnabled && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl p-3 flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <p className="text-xs text-green-700 dark:text-green-300">
                    Daily reminders enabled for {settings.reminderTime.replace(/^0/, '')}
                  </p>
                </div>
              )}

              {/* Email Myself Button */}
              <button
                onClick={handleEmailMyself}
                className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-xl border border-emerald-200 dark:border-emerald-700 hover:shadow-md transition-all"
                data-testid="email-myself-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-emerald-800 dark:text-emerald-300 text-sm">Email Myself a Reminder</p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">Opens your email app</p>
                  </div>
                </div>
                <Send className="w-5 h-5 text-emerald-500" />
              </button>

              {/* Add to Calendar Button */}
              <button
                onClick={handleScheduleReminder}
                className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-xl border border-violet-200 dark:border-violet-700 hover:shadow-md transition-all"
                data-testid="add-calendar-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-violet-800 dark:text-violet-300 text-sm">Add to Google Calendar</p>
                    <p className="text-xs text-violet-600 dark:text-violet-400">Daily recurring event for 30 days</p>
                  </div>
                </div>
                <ExternalLink className="w-5 h-5 text-violet-500" />
              </button>
            </div>

            {/* Success Message */}
            {showSuccess && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl p-3 flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600" />
                <p className="text-sm text-green-700 dark:text-green-300">Email app opened!</p>
              </div>
            )}

            {/* PWA Tip */}
            <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <Smartphone className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-700 dark:text-gray-300 font-medium mb-1">
                    💡 Pro Tip
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    For the most reliable reminders, add a recurring event to your Google Calendar using the button above.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="p-4 bg-gray-50 dark:bg-[var(--muted)] border-t border-gray-100 dark:border-[var(--border)] sticky bottom-0">
            <button
              onClick={onClose}
              className="w-full py-3 bg-[var(--primary)] text-white rounded-xl font-medium hover:bg-[var(--primary-dark)] transition-colors flex items-center justify-center gap-2"
              data-testid="done-btn"
            >
              <Check className="w-4 h-4" />
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationSettingsModal;
